/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {
  type AbsoluteURL,
  type CoreService,
  coreServiceToFlow,
  type FlowRunStatus,
  gisFlows,
  type LastSimulationRecord,
  paBatchFlows,
  paFlows,
  plantSimFlows,
  type RecentConfigRecord,
} from '@poweralpha/common';
import { getPlantSweepPath } from '../plant-v2/paths';
import { runReportPath, runsPath, sweepPath } from '../project-simulate/paths';
import { serviceNameToResultURL } from '../simulation-status/routes';
import { parseProjectIdFromTags } from '../simulation-status/utils';

export const getSimulationLink = (simulation: LastSimulationRecord): AbsoluteURL => {
  switch (simulation.service) {
    case 'PVSim':
      return `/pv/pvsim/result?scenario=${simulation.scenario_id}`;
    case 'PVCosting':
      return `/pv/costing/simulate?scenario=${simulation.scenario_id}`;
    case 'ESS':
      return `/ess/costing/simulate?scenario=${simulation.scenario_id}`;
    case 'PlantSim':
      return `/plant/results?scenario=${simulation.scenario_id}`;
    case 'Project':
      return simulation.project_id
        ? runReportPath(simulation.project_id, simulation.scenario_id)
        : '/projects';
    default:
      return '/projects';
  }
};

export const getConfigLink = (config: RecentConfigRecord): AbsoluteURL => {
  const queryParams = `config=${config.id}&version=${config.version}`;
  switch (config.service) {
    case 'PVSim':
      return `/pv/pvsim/new?${queryParams}`;
    case 'PVCosting':
      return `/pv/costing/simulate?${queryParams}`;
    case 'ESS':
      return `/ess/costing/simulate?${queryParams}`;
    case 'PlantSim':
      return `/plant/simulate?${queryParams}`;
    default:
      return '/projects';
  }
};

const projectBatchFlows = new Set<string>([...paBatchFlows, ...paFlows, ...gisFlows]);
const plantFlows = new Set<string>(plantSimFlows);
const coreServiceByFlow = new Map<string, CoreService>(
  Object.entries(coreServiceToFlow).map(([service, flow]) => [flow, service as CoreService]),
);

export const getBatchRunLink = (run: FlowRunStatus): AbsoluteURL | null => {
  if (projectBatchFlows.has(run.flow_name)) {
    const projectId = parseProjectIdFromTags(run.tags);
    if (!projectId) return '/projects';
    if (paBatchFlows.includes(run.flow_name as (typeof paBatchFlows)[number])) {
      return sweepPath(projectId, run.id);
    }
    return runsPath(projectId, 'sweep');
  }

  if (plantFlows.has(run.flow_name)) {
    switch (run.flow_name) {
      case 'PlantSim Batch':
        return '/plant/batch/results';
      case 'PlantSim Net Benefit':
        return '/plant/net-benefit';
      case 'PlantSim v2 Batch':
        return getPlantSweepPath(run.id);
      case 'PlantSim v2 Net Benefit':
        return '/plant-v2/net-benefit';
      case 'PlantSim v2':
        return '/plant-v2/results';
      default:
        return '/plant/results';
    }
  }

  // Single service runs: their result list, where the failed run shows with its error.
  const service = coreServiceByFlow.get(run.flow_name);
  return service ? serviceNameToResultURL[service] : null;
};
